import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { ChevronDown, Check, Eye, EyeOff, ArrowLeft, Search, Loader2 } from "lucide-react";
import { FiCheckCircle } from "react-icons/fi";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../../api/api"
import { clearPendingJoin } from "../../utils/pendingJoin";
import VerifyCodeForm from "./VerifyCodeForm";
import MultiSelect from "../../components/ui/MultiSelect";
import MembershipCard from "../../components/MembershipCard";
//import { MEMBERSHIP_FEATURES, getFeaturesForTier } from "../../utils/membershipFeatures";
import { MEMBERSHIP_FEATURES, getFeaturesForTier, withTierAccess } from "../../utils/membershipFeatures";


const EMPTY_LOOKUPS = { personas: {}, intents: {}, industries: {}, positions: [], locations: [] };

// US states for the coach-signup "State" dropdown. Value stored = the full name
// (readable for admins reviewing pending coaches).
const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
    "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
    "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts",
    "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
    "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
    "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming",
];
const US_STATE_OPTIONS = US_STATES.map((s) => ({ value: s, label: s }));

// Coach credential types (radio group at signup).
const COACH_CREDENTIALS = [
    { value: "ICF", label: "International Coaching Federation (ICF)" },
    { value: "Gallup", label: "Gallup StrengthFinder" },
];
// The certificate-URL example switches by credential. A Gallup coach's
const normalizeLookupPayload = (payload) => ({
    personas: payload?.personas && typeof payload.personas === "object" ? payload.personas : {},
    intents: payload?.intents && typeof payload.intents === "object" ? payload.intents : {},
    industries: payload?.industries && typeof payload.industries === "object" ? payload.industries : {},
    positions: Array.isArray(payload?.positions) ? payload.positions : [],
    locations: Array.isArray(payload?.locations) ? payload.locations : [],
});


function SingleSelect({
    label,
    value,
    options,
    placeholder,
    onChange,
    error,
    disabled = false,
    helperText,
    renderValue,
    isDarkMode,
    rightLabel,
    required = false,
    maxVisibleOptions = 6,
    // Opt-in: adds a filter input above the option list, matching the
    // searchable MultiSelect dropdowns used on the Preferences page.
    // Meant for longer option lists (e.g. the 50-state list) where
    // scanning an unfiltered list isn't practical.
    searchable = false,
    searchPlaceholder = "Search...",
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) setSearchTerm("");
    }, [isOpen]);

    const selectedOption = options.find((option) => option.value === value) || null;
    // Prefix match, not substring — typing "t" should jump straight to the
    // T's (Tennessee, Texas), the way a native <select> jumps on keypress,
    // rather than surfacing every state with a "t" in it.
    const visibleOptions = searchable && searchTerm.trim()
        ? options.filter((option) => option.label.toLowerCase().startsWith(searchTerm.trim().toLowerCase()))
        : options;

    return (
        <div className="space-y-2">
            <label className="text-sm font-semibold mb-2 flex items-center">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
                {rightLabel}
            </label>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen((prev) => !prev)}
                    disabled={disabled}
                    className={`w-full p-3 border rounded-xl transition-colors text-left flex items-center justify-between ${
                        error ? 'border-red-500' : (isDarkMode ? 'border-slate-700' : 'border-slate-300')
                    } ${
                        disabled
                            ? (isDarkMode ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed')
                            : (isDarkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900')
                    }`}
                >
                    <span className={selectedOption ? '' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}>
                        {selectedOption ? (renderValue ? renderValue(selectedOption) : selectedOption.label) : placeholder}
                    </span>
                    <ChevronDown
                        size={18}
                        className={`transition-transform ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                    />
                </button>

                {isOpen && !disabled && (
                    <div className={`absolute top-full left-0 z-50 mt-2 w-full rounded-xl border shadow-xl overflow-hidden ${
                        isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
                    }`}>
                        {searchable && (
                            <div className={`sticky top-0 p-2 border-b ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                <div className="relative">
                                    <Search
                                        size={16}
                                        className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}
                                    />
                                    <input
                                        type="text"
                                        autoFocus
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder={searchPlaceholder}
                                        className={`w-full pl-8 pr-3 py-2 rounded-lg text-sm outline-none border ${
                                            isDarkMode
                                                ? 'bg-slate-800 border-slate-600 text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-primary-500/40'
                                                : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-100'
                                        }`}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="overflow-auto" style={{ maxHeight: `${Math.max(1, maxVisibleOptions) * 48}px` }}>
                            {options.length === 0 ? (
                                <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    No options available
                                </div>
                            ) : visibleOptions.length === 0 ? (
                                <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    No matches found
                                </div>
                            ) : (
                                visibleOptions.map((option) => {
                                    const selected = option.value === value;
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => {
                                                onChange(option.value);
                                                setIsOpen(false);
                                            }}
                                            className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${
                                                isDarkMode
                                                    ? `${selected ? 'bg-primary-900/30' : 'bg-slate-900'} hover:bg-slate-800 text-slate-100`
                                                    : `${selected ? 'bg-primary-50' : 'bg-white'} hover:bg-slate-50 text-slate-900`
                                            }`}
                                        >
                                            <span>{option.label}</span>
                                            {selected && <Check size={16} className={isDarkMode ? 'text-primary-400' : 'text-primary-600'} />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
            {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
            {!error && helperText && <p className="text-xs text-slate-500">{helperText}</p>}
        </div>
    );
}


/** The plan step's loading state (stage 3): its heading when asked for, and three
 *  plan cards, like the coach plans page's skeleton. */
function PlanStepSkeleton({ isDarkMode, heading = false }) {
    const baseColor = isDarkMode ? "#334155" : "#e2e8f0";
    const highlightColor = isDarkMode ? "#475569" : "#f1f5f9";
    return (
        <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>
            <div aria-busy="true" aria-label="Loading plans">
                {heading && (
                    <>
                        <div className="max-w-sm mb-2"><Skeleton height={36} /></div>
                        <div className="max-w-xl mb-8"><Skeleton height={18} /></div>
                    </>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className={`rounded-2xl border p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
                            <Skeleton width={110} height={18} />
                            <Skeleton width={130} height={34} className="mt-3" />
                            <div className="space-y-2 mt-5">
                                {[0, 1, 2, 3, 4].map((r) => <Skeleton key={r} height={13} />)}
                            </div>
                            <Skeleton height={42} borderRadius={12} className="mt-6" />
                        </div>
                    ))}
                </div>
            </div>
        </SkeletonTheme>
    );
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const nameRegex = /^[a-zA-Z\-']+$/;
const NAME_CHAR_ERROR = "Names can only contain letters, hyphens, and apostrophes.";
const getNameCharError = (value) => (value && !nameRegex.test(value) ? NAME_CHAR_ERROR : null);

export default function Signup() {
    const { checkAuth, user } = useAuth();
    const [signupType, setSignupType] = useState('student'); // 'student' | 'coach'
    const [searchParams] = useSearchParams();
    const [stage2SessionMissing, setStage2SessionMissing] = useState(false);
    const fromDashboard = searchParams.get("fromDashboard");
    const isDashboardOnboardingMode = fromDashboard === "1" || fromDashboard === "2";
    /** `stage=2` is set when the user lands here after verifying their email code (e.g. from /verify-email → /signup?stage=2). */
    const isStage2FromUrl = searchParams.get("stage") === "2";
    const verifiedFromEmail = searchParams.get("verified") === "1";
    /** `code` comes from a program invitation link (/join/<code>); the new student joins that program after the profile step.
     *  It can also be the student's own license code from their organization (preview kind "license"), used at the same step. */
    const inviteCode = (searchParams.get("code") || "").trim();
    const [invitedProgram, setInvitedProgram] = useState(null);
    // true, or "used": a license code someone already redeemed. Either way the code
    // isn't sent with stage 1 (only a code that previewed fine is, as invitedProgram).
    const [inviteInvalid, setInviteInvalid] = useState(false);

    useEffect(() => {
        if (!inviteCode) return;
        // Signing up through the link joins the program at the profile step
        clearPendingJoin();
        let cancelled = false;
        api.get(`/org/programs/preview?code=${encodeURIComponent(inviteCode)}`)
            .then((res) => {
                if (cancelled) return;
                // A license code previews as { kind: "license", OrgName, ProgramName }
                setInvitedProgram(res.data.kind === "license" ? res.data : res.data.program);
                setSignupType('student');
            })
            .catch((err) => { if (!cancelled) setInviteInvalid(err.response?.data?.code === "CODE_USED" ? "used" : true); });
        return () => { cancelled = true; };
    }, [inviteCode]);
    const [stage, setStage] = useState(() => {
        if (isDashboardOnboardingMode) return 2;
        if (isStage2FromUrl) return 2;
        return 1;
    });
    const [isDarkMode] = useState(false);
    const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
    const [isLoadingLookups, setIsLoadingLookups] = useState(true);
    const [registeredEmail, setRegisteredEmail] = useState("");

    // Stage 3: membership selection (Free / Pro / Beta)
    const [onboardingTiers, setOnboardingTiers] = useState([]);
    const [isLoadingTiers, setIsLoadingTiers] = useState(false);
    const [selectedTierId, setSelectedTierId] = useState(null);
    const [tierAttemptId, setTierAttemptId] = useState(null);
    // A promo code typed at signup (stage 1): previewed on the plan step, then
    // claimed (free code) or sent to checkout (percent/amount code)
    const [signupPromoCode, setSignupPromoCode] = useState("");
    const [signupPromo, setSignupPromo] = useState(null);
    const [signupPromoError, setSignupPromoError] = useState("");
    const [tierSubmitting, setTierSubmitting] = useState(false);
    const [tierError, setTierError] = useState("");

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const response = await api.get("/meta/lookups");
                setLookups(normalizeLookupPayload(response.data));
            } catch (err) {
                console.error("Failed to load lookups", err);
                setLookups(EMPTY_LOOKUPS);
            } finally {
                setIsLoadingLookups(false);
            }
        };
        fetchLookups();
    }, []);

    // After email verification, backend redirects to /signup?stage=2 (session has pending_client_id).
    // Keep UI in sync if search params change (e.g. full page load or client navigation).
    useEffect(() => {
        if (isDashboardOnboardingMode) {
            setStage(2);
            return;
        }
        if (searchParams.get("stage") === "2") {
            setStage(2);
        }
    }, [isDashboardOnboardingMode, searchParams]);

    // Refresh profile flags from the server so App routing and stage-2 submit use up-to-date onboarding state.
    useEffect(() => {
        if (stage !== 2 || isDashboardOnboardingMode) return;
        checkAuth();
    }, [stage, isDashboardOnboardingMode, checkAuth]);

    // Stage 2: pending signups are not "logged in", so GET /user/me is 401 — use a session check that
    // matches POST /auth/signup/stage2 (pending_client_id or logged-in incomplete client).
    useEffect(() => {
        if (stage !== 2 || isDashboardOnboardingMode) {
            setStage2SessionMissing(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/signup-stage2-session");
                if (!cancelled) {
                    setStage2SessionMissing(data?.canCompleteStage2 !== true);
                }
            } catch {
                if (!cancelled) setStage2SessionMissing(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [stage, isDashboardOnboardingMode]);

    useEffect(() => {
        if (!isDashboardOnboardingMode) {
            return;
        }

        const preloadOnboarding = async () => {
            try {
                const response = await api.get("/user/onboarding");
                const data = response.data || {};
                const firstPosition = Array.isArray(data.positions) && data.positions.length > 0 ? data.positions[0] : "";
                const industries = Array.isArray(data.industries)
                    ? data.industries.filter(Boolean)
                    : (data.industries ? [data.industries] : []);

                setProfile((prev) => ({
                    ...prev,
                    aspiringRole: firstPosition || prev.aspiringRole,
                    industries: industries.length ? industries : prev.industries,
                    persona: data.persona || prev.persona,
                    intent: data.intent || prev.intent,
                }));
                setSavedLocationIds(Array.isArray(data.job_locations) ? data.job_locations : []);

                // Recovery path: a user who already finished the profile step but
                // dropped off before picking a membership gets routed back here by
                // OnboardingGate. Don't make them redo the profile form — jump
                // straight to the membership picker. "Finished the profile step"
                // means either the profile data is all there, OR the server flags
                // the profile gate as passed (user.profileComplete) while only the
                // plan is missing (user.onboardingComplete === false) — e.g. the
                // user chose "Skip for now", so there's no data but they must NOT
                // be dropped back on Profile Setup; they only need to pick a plan.
                const profileDataComplete = Boolean(firstPosition) && Boolean(data.persona) && Boolean(data.intent) && industries.length > 0;
                const onlyNeedsPlan = user?.profileComplete !== false && user?.onboardingComplete === false;
                if (profileDataComplete || onlyNeedsPlan) {
                    setStage(3);
                }
            } catch {
                // If preload fails, keep the stage 2 form empty and user-editable.
            }
        };

        preloadOnboarding();
    }, [isDashboardOnboardingMode, user]);

    // To display them in your Stage 2 Form:
    // "testing adding" and "Persona_Default"/"Default_Intent"/
    // "Default_Industry"/"Testingediting&add" are seeded placeholder rows
    // in the Personas/Intents/Industries tables, not real selectable
    // options — hide them from the signup dropdowns.
    const normalizeLookupName = (name) => name.replace(/[\s_&]/g, '').toLowerCase();
    const EXCLUDED_PERSONA_NAMES = ['testingadding', 'personadefault'];
    const EXCLUDED_INTENT_NAMES = ['defaultintent'];
    const EXCLUDED_INDUSTRY_NAMES = ['defaultindustry', 'testingeditingadd'];
    const industryNames = Object.keys(lookups.industries)
        .filter((name) => !EXCLUDED_INDUSTRY_NAMES.includes(normalizeLookupName(name)))
        .sort((a, b) => a.localeCompare(b));
    const personaNames = Object.keys(lookups.personas)
        .filter((name) => !EXCLUDED_PERSONA_NAMES.includes(normalizeLookupName(name)))
        .sort((a, b) => a.localeCompare(b));
    const intentNames = Object.keys(lookups.intents)
        .filter((name) => !EXCLUDED_INTENT_NAMES.includes(normalizeLookupName(name)))
        .sort((a, b) => a.localeCompare(b));
    const jobRoles = Array.isArray(lookups.positions) ? lookups.positions : [];
    const roleOptions = jobRoles.map((role) => ({ value: role.name, label: role.name }));
    const industryOptions = industryNames.map((name) => ({ value: name, label: name }));
    const personaOptions = personaNames.map((name) => ({ value: name, label: name }));
    const intentOptions = intentNames.map((name) => ({ value: name, label: name }));
    const locationsByState = lookups.locations || []; // Locations grouped by state

    // Location selection state (up to 3 preferred locations)
    const [selectedLocations, setSelectedLocations] = useState([]); // [{id, city, state}]
    const [currentState, setCurrentState] = useState("");
    const [currentCity, setCurrentCity] = useState("");
    // Saved preferred locations (ids from /user/onboarding) become chips once the
    // location lookups have loaded
    const [savedLocationIds, setSavedLocationIds] = useState([]);
    useEffect(() => {
        const states = lookups.locations || [];
        if (!savedLocationIds.length || !states.length) return;
        const found = [];
        states.forEach((s) => (s.cities || []).forEach((c) => {
            if (savedLocationIds.includes(c.id)) found.push({ id: c.id, city: c.name, state: s.state });
        }));
        setSelectedLocations((prev) => (prev.length ? prev : found.slice(0, 3)));
        setSavedLocationIds([]);
    }, [savedLocationIds, lookups.locations]);

    const citiesForState = locationsByState.find(s => s.state === currentState)?.cities || [];

    const addLocation = () => {
        if (!currentCity || selectedLocations.length >= 3) return;
        const cityObj = citiesForState.find(c => c.id === parseInt(currentCity));
        if (!cityObj || selectedLocations.some(l => l.id === cityObj.id)) return;
        setSelectedLocations([...selectedLocations, { id: cityObj.id, city: cityObj.name, state: currentState }]);
        setCurrentCity("");
    };

    const removeLocation = (locId) => {
        setSelectedLocations(selectedLocations.filter(l => l.id !== locId));
    };


    function InfoTooltip({ text, isDarkMode }) {
        return (
            <div className="group relative inline-block ml-2 cursor-help">
            <span className={`text-xs border rounded-full px-1.5 py-0.5 font-bold ${isDarkMode ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
                ?
            </span>
                <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 text-xs rounded-lg shadow-xl z-50 ${
                    isDarkMode ? 'bg-slate-700 text-slate-100 border border-slate-600' : 'bg-white text-slate-700 border border-slate-200'
                }`}>
                    {text}
                    <div className={`absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent ${
                        isDarkMode ? 'border-t-slate-700' : 'border-t-white'
                    }`}></div>
                </div>
            </div>
        );
    }
    // Stage 1 State: Credentials
    const [creds, setCreds] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        referralCode: "",
        acceptedTerms: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [stage1Submitting, setStage1Submitting] = useState(false);

    /** `invite` comes from an organization's invitation email: the account must use
     *  the invited address, and the organization is joined once the profile step is done. */
    const inviteToken = (searchParams.get("invite") || "").trim();
    const [emailInvite, setEmailInvite] = useState(null);
    const [emailInviteError, setEmailInviteError] = useState(null); // {message, used}

    useEffect(() => {
        if (!inviteToken) return;
        let cancelled = false;
        api.get(`/org/invites/preview?token=${encodeURIComponent(inviteToken)}`)
            .then((res) => {
                if (cancelled) return;
                const invite = res.data.invite;
                setEmailInvite(invite);
                setCreds((c) => ({ ...c, email: invite.Email }));
                setSignupType(invite.InviteType === 'coach' ? 'coach' : 'student');
            })
            .catch((err) => {
                if (cancelled) return;
                setEmailInviteError({
                    message: err.response?.data?.error || "This invitation link doesn't work anymore.",
                    used: err.response?.data?.code === "INVITE_USED",
                });
            });
        return () => { cancelled = true; };
    }, [inviteToken]);

    // Coach-only stage-1 fields (credential info). Ignored for student signups.
    const [coachInfo, setCoachInfo] = useState({
        credentialType: "",   // 'ICF' | 'Gallup'
        certificateUrl: "",
        companyName: "",
        state: "",
        directoryEmail: "",
        directorySameAsSignIn: true,
        attested: false,
    });

    // Drives the Stage 1 submit button's disabled/gray state — a lightweight
    // presence check (not the full validateStage1 regex rules) so the button
    // doesn't need every keystroke re-run through the validator just to decide
    // whether it's clickable yet.
    // A coach invited by an organization doesn't need a certification (the
    // organization vouches for them); Build My Biz Brand still approves them.
    const invitedCoach = emailInvite?.InviteType === 'coach';
    const isStage1Ready = signupType === 'coach'
        ? Boolean(
            creds.firstName && creds.lastName && creds.email && creds.password && creds.acceptedTerms &&
            (invitedCoach || (coachInfo.credentialType && coachInfo.certificateUrl.trim() && coachInfo.attested)) &&
            coachInfo.state && (coachInfo.directorySameAsSignIn || coachInfo.directoryEmail.trim())
        )
        : Boolean(creds.firstName && creds.lastName && creds.email && creds.password && creds.acceptedTerms);

    // Stage 2 State: Profile Details
    const [profile, setProfile] = useState({
        aspiringRole: "",
        industries: [],
        persona: "",
        intent: "",
        linkedinProfile: "",
        meetupAccount: "",
    });
    const [stage2Submitting, setStage2Submitting] = useState(false);

    // Drives the Stage 2 submit button's disabled/gray state — mirrors the
    // required-field checks at the top of submitStage2.
    const isStage2Ready = Boolean(
        profile.aspiringRole && profile.persona && profile.intent && profile.industries.length > 0
    );

    const validateStage1 = (values) => {
        const newErrors = {};
        // Hyphen and apostrophe removed: both are valid in hyphenated/apostrophe names
        // (e.g. Smith-Jones, O'Brien) and in emails, so the name-character check above
        // is what actually gates those, not this list.
        const sqlInjectionRegex = /[";\\]/i;
        const MIN_PASSWORD_LENGTH = 8;

        // First Name Validation
        if (!values.firstName) {
            newErrors.firstName = "First name is required.";
        } else if (values.firstName.length > 100) {
            newErrors.firstName = "First name exceeds 100 characters.";
        } else if (values.firstName.includes(' ')) {
            newErrors.firstName = "First name cannot contain spaces.";
        } else if (!nameRegex.test(values.firstName)) {
            newErrors.firstName = "Names can only contain letters, hyphens, and apostrophes.";
        } else if (!/^[A-Z]/.test(values.firstName)) {
            newErrors.firstName = "First name must start with a capital letter.";
        } else if (sqlInjectionRegex.test(values.firstName)) {
            newErrors.firstName = "First name contains unsafe characters.";
        }

        // Last Name Validation
        if (!values.lastName) {
            newErrors.lastName = "Last name is required.";
        } else if (values.lastName.length > 100) {
            newErrors.lastName = "Last name exceeds 100 characters.";
        } else if (values.lastName.includes(' ')) {
            newErrors.lastName = "Last name cannot contain spaces.";
        } else if (!nameRegex.test(values.lastName)) {
            newErrors.lastName = "Names can only contain letters, hyphens, and apostrophes.";
        } else if (!/^[A-Z]/.test(values.lastName)) {
            newErrors.lastName = "Last name must start with a capital letter.";
        } else if (sqlInjectionRegex.test(values.lastName)) {
            newErrors.lastName = "Last name contains unsafe characters.";
        }

        // Email Validation
        if (!values.email) {
            newErrors.email = "Email address is required.";
        } else if (values.email.includes(' ')) {
            newErrors.email = "Email cannot contain spaces.";
        } else if (!emailRegex.test(values.email)) {
            newErrors.email = "Please enter a valid email address format (e.g., user@domain.com).";
        } else if (sqlInjectionRegex.test(values.email)) {
            newErrors.email = "Email contains potentially unsafe characters.";
        }

        // Password Validation — no character-blocklist here: SQL injection is a
        // query-input concern (email/name fields feed queries elsewhere), and the
        // backend hashes passwords rather than interpolating them into SQL, so
        // restricting characters here only weakens the passwords users can pick.
        if (!values.password) {
            newErrors.password = "Password is required.";
        } else if (values.password.includes(' ')) {
            newErrors.password = "Password cannot contain spaces.";
        } else if (values.password.length < MIN_PASSWORD_LENGTH) {
            newErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
        } else if (!/[A-Z]/.test(values.password)) {
            newErrors.password = "Password must contain at least one uppercase letter.";
        }

        // Required for every signup type: forced client-side so the account
        // can't be created without accepting, regardless of signupType.
        if (!values.acceptedTerms) {
            newErrors.acceptedTerms = "You must accept the Terms of Service and Privacy Policy to continue.";
        }

        // Coach-only required fields (no URL format check per spec).
        if (signupType === 'coach') {
            if (!invitedCoach && !coachInfo.credentialType) {
                newErrors.credentialType = "Please choose a credential type.";
            }
            if (!invitedCoach && !coachInfo.certificateUrl.trim()) {
                newErrors.certificateUrl = "A certificate URL is required.";
            }
            if (!coachInfo.state) {
                newErrors.state = "Please select your state.";
            }
            if (!coachInfo.directorySameAsSignIn && !coachInfo.directoryEmail.trim()) {
                newErrors.directoryEmail = "Enter your directory email or check “Same as my sign-in email”.";
            }
            if (!invitedCoach && !coachInfo.attested) {
                newErrors.attested = "You must attest to your certification and consent to be listed.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [errors, setErrors] = useState({});
    const [emailTouched, setEmailTouched] = useState(false);

    // Sanitization helper functions
    const capitalizeFirst = (str) => {
        if (!str || str.length === 0) return str;
        // Ensure first character is uppercase, preserve the rest as typed
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const removeSpaces = (str) => {
        return str.replace(/\s/g, '');
    };

    // Form Handlers with sanitization
    const handleCredChange = (e) => {
        const { name, value } = e.target;
        let sanitizedValue = value;

        // Remove spaces from all fields except referralCode
        if (name !== 'referralCode') {
            sanitizedValue = removeSpaces(sanitizedValue);
        } else {
            sanitizedValue = sanitizedValue.trim().toUpperCase();
        }

        // Capitalize first character for names (only if it's not already capitalized)
        if (name === 'firstName' || name === 'lastName') {
            if (sanitizedValue.length > 0) {
                sanitizedValue = capitalizeFirst(sanitizedValue);
            }
        }

        setCreds({ ...creds, [name]: sanitizedValue });

        // Clear this field's submit-time error ("… is required.") the moment the
        // user edits it, so a stale error never lingers on a field they've since
        // filled — that used to contradict the live "✓ Valid email" / "Requirements
        // Met" hints. Names also get a live invalid-character check re-applied here.
        setErrors((prev) => {
            const isName = name === 'firstName' || name === 'lastName';
            const charError = isName ? getNameCharError(sanitizedValue) : null;
            if (!(name in prev) && !charError) return prev;
            const next = { ...prev };
            delete next[name];
            if (charError) next[name] = charError;
            return next;
        });
    };

    const handleProfileFieldChange = (name, value) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
        setProfile((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCoachFieldChange = (name, value) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
        setCoachInfo((prev) => ({ ...prev, [name]: value }));
    };
    // Stage 1 Submit: Create Basic Account
    const submitStage1 = async (e) => {
        e.preventDefault();

        const isValid = validateStage1(creds);
        if (!isValid) return;

        setStage1Submitting(true);
        try {
            // Coach signup: create a *pending* coach (Admin AdminTypeID=6 + Partners,
            // both inactive until an admin approves) and email an OTP. Different
            // endpoint + payload from the student flow; still goes through the shared
            // "verify" step next.
            if (signupType === 'coach') {
                try {
                    const response = await api.post("/auth/signup/coach", {
                        first_name: creds.firstName,
                        last_name: creds.lastName,
                        email: creds.email,
                        password: creds.password,
                        credential_type: coachInfo.credentialType,
                        certificate_url: coachInfo.certificateUrl.trim(),
                        company_name: coachInfo.companyName.trim() || undefined,
                        state: coachInfo.state,
                        directory_email: (coachInfo.directorySameAsSignIn ? creds.email : coachInfo.directoryEmail.trim()) || undefined,
                        is_attest: coachInfo.attested,
                        invite_token: invitedCoach ? inviteToken : undefined,
                    }, { withCredentials: true, timeout: 20000 }); // Give email provider time

                    if (response.status === 201) {
                        setRegisteredEmail(creds.email);
                        setStage("verify");
                    }
                } catch (err) {
                    setErrors({ server: err.response?.data?.error || "Error creating account" });
                }
                return;
            }

            try {
                // Mapping camelCase to snake_case for the backend
                const response = await api.post("/auth/signup/stage1", {
                    first_name: creds.firstName,
                    last_name: creds.lastName,
                    email: creds.email,
                    password: creds.password,
                    referral_code: creds.referralCode || undefined,
                    invite_code: invitedProgram ? inviteCode : undefined
                }, { withCredentials: true, timeout: 20000 }); // Give email provider time

                if (response.status === 201) {
                    setRegisteredEmail(creds.email);
                    if (response.data?.promoCode) setSignupPromoCode(response.data.promoCode);
                    setStage("verify");
                    // setStage(2);
                }
            } catch (err) {
                setErrors({ server: err.response?.data?.error || "Error creating account" });
            }
        } finally {
            setStage1Submitting(false);
        }
    };

    // Stage 2 Submit: Profile setup (complete signup)
    const submitStage2 = async (e) => {
        e.preventDefault();
        // One profile request at a time: a second one sent before the first
        // returns can come back last and sign the new member out again
        if (stage2Submitting || skipLoading) return;

        // Validate required fields
        if (!profile.aspiringRole) {
            setErrors({ aspiringRole: "Please select an aspiring role." });
            return;
        }
        if (!profile.persona) {
            setErrors({ persona: "Please select a persona." });
            return;
        }
        if (!profile.intent) {
            setErrors({ intent: "Please select an intent." });
            return;
        }
        if (profile.industries.length === 0) {
            setErrors({ industries: "Please select at least one industry." });
            return;
        }

        // Clear any previous errors
        setErrors({});

        setStage2Submitting(true);
        try {
            if (isDashboardOnboardingMode) {
                const response = await api.post("/user/onboarding", {
                    PersonaName: profile.persona,
                    IntentName: profile.intent,
                    IndustryNames: profile.industries,
                    aspiring_role: profile.aspiringRole || "",
                    roles: profile.aspiringRole ? [profile.aspiringRole] : [],
                    positions: profile.aspiringRole ? [profile.aspiringRole] : [],
                    job_locations: selectedLocations.map(l => l.id)
                }, { withCredentials: true });

                if (response.status === 200) {
                    setStage(3);
                }
            } else {
                // Dynamically find IDs from our fetched lookups
                const selectedPersonaId = lookups.personas[profile.persona]?.id;
                const selectedIntentId = lookups.intents[profile.intent]?.id;

                // For industries, map names in profile.industries to IDs
                const selectedIndustryIds = profile.industries.map(name => lookups.industries[name]);

                const response = await api.post("/auth/signup/stage2", {
                    aspiring_role: profile.aspiringRole,
                    industry_ids: selectedIndustryIds,
                    persona_id: selectedPersonaId,
                    intent_id: selectedIntentId,
                    location_ids: selectedLocations.map(l => l.id),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    onboarding_task_ids: []
                }, { withCredentials: true });

                if (response.status === 200) {
                    setStage(3);
                }
            }
        } catch (err) {
            setErrors({ server: err.response?.data?.error || "Failed to complete signup." });
        } finally {
            setStage2Submitting(false);
        }
    };

    // Stage 2 Skip: same endpoints as submitStage2, but with everything left
    // null/empty instead of validated. The user can fill in real answers
    // later from their profile — this just gets them into the app now.
    const [skipLoading, setSkipLoading] = useState(false);
    const skipStage2 = async () => {
        if (skipLoading || stage2Submitting) return; // one profile request at a time
        setErrors({});
        setSkipLoading(true);
        try {
            if (isDashboardOnboardingMode) {
                const response = await api.post("/user/onboarding", {
                    PersonaName: null,
                    IntentName: null,
                    IndustryNames: [],
                    aspiring_role: "",
                    roles: [],
                    positions: [],
                    job_locations: []
                }, { withCredentials: true });

                if (response.status === 200) {
                    setStage(3);
                }
            } else {
                const response = await api.post("/auth/signup/stage2", {
                    aspiring_role: "",
                    industry_ids: [],
                    persona_id: null,
                    intent_id: null,
                    location_ids: [],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    onboarding_task_ids: []
                }, { withCredentials: true });

                if (response.status === 200) {
                    setStage(3);
                }
            }
        } catch (err) {
            setErrors({ server: err.response?.data?.error || "Failed to skip onboarding." });
        } finally {
            setSkipLoading(false);
        }
    };

    // Real escape from the forced profile step. Marks the profile step as
    // deferred (SignupComplete) on the server so OnboardingGate stops forcing it,
    // THEN goes to the dashboard — without this mark, /userdashboard just bounces
    // straight back here (forceOnboarding). Does NOT touch answers already saved.
    const [backLoading, setBackLoading] = useState(false);
    const backToDashboard = async () => {
        setBackLoading(true);
        try {
            await api.post("/user/onboarding/skip", {}, { withCredentials: true });
        } catch {
            // Best-effort: let the user leave rather than trapping them here.
        }
        window.location.href = "/userdashboard";
    };

    // Stage 3: fetch the tiers eligible for the onboarding picker (Free / Pro /
    // Beta if the rollout is still open) once we land on this stage.
    useEffect(() => {
        if (stage !== 3) return;
        let cancelled = false;
        setIsLoadingTiers(true);
        api.get("/membership/onboarding-tiers")
            .then(({ data }) => {
                if (cancelled) return;
                const tiers = Array.isArray(data?.tiers) ? data.tiers : [];
                setOnboardingTiers(tiers);
                if (data?.promoCode) setSignupPromoCode(data.promoCode);
                const beta = tiers.find((t) => t.TierName === "Beta User");
                const pro = tiers.find((t) => t.TierName === "Pro");
                const defaultTier = beta || pro || tiers[0];
                if (defaultTier) setSelectedTierId(defaultTier.TierID);
            })
            .catch(() => {
                if (!cancelled) setOnboardingTiers([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingTiers(false);
            });
        return () => { cancelled = true; };
    }, [stage]);

    // Stage 3: a student whose organization gave them a license (used at the profile
    // step) has their plan covered, so there's no plan to pick or pay for.
    // undefined while checking; null shows the plan cards (also when the check fails).
    const [coveredBy, setCoveredBy] = useState(undefined);
    useEffect(() => {
        if (stage !== 3) return;
        let cancelled = false;
        setCoveredBy(undefined);
        api.get("/org/context")
            .then(({ data }) => {
                if (cancelled) return;
                const orgs = data?.role === "student" && Array.isArray(data.organizations) ? data.organizations : [];
                // Only a license that comes with a plan covers it (an org can give licenses with no plan)
                setCoveredBy(orgs.find((o) => o.HasLicense && o.SponsoredTierName) || null);
            })
            .catch(() => { if (!cancelled) setCoveredBy(null); });
        return () => { cancelled = true; };
    }, [stage]);

    // The covered panel's button: the same as picking the free plan on this step
    // (the organization's license gives them its plan while it's active)
    const continueCovered = async () => {
        setTierError("");
        setTierSubmitting(true);
        try {
            const freeTier = onboardingTiers.find((t) => Number(t.Price) === 0 && !t.TrialDays);
            // Without a free plan in the list, the server grants the default one
            await api.post("/membership/select-free-tier", freeTier ? { tier_id: freeTier.TierID } : {}, { withCredentials: true });
            window.location.href = "/membership/free-success";
        } catch (err) {
            setTierError(err.response?.data?.error || "Could not continue. Please try again.");
            setTierSubmitting(false);
        }
    };

    useEffect(() => {
        if (stage !== 3 || !signupPromoCode || onboardingTiers.length === 0) return;
        let cancelled = false;
        api.post("/membership/code/preview", { code: signupPromoCode })
            .then(({ data }) => {
                if (cancelled) return;
                const covered = (data?.tiers || []).filter((t) => onboardingTiers.some((x) => x.TierID === t.TierID));
                if (covered.length === 0) {
                    setSignupPromo(null);
                    setSignupPromoError(`${signupPromoCode} isn't for these plans.`);
                } else {
                    setSignupPromo({ ...data, tiers: covered });
                    setSignupPromoError("");
                }
            })
            .catch((err) => {
                if (!cancelled) setSignupPromoError(err.response?.data?.error || `${signupPromoCode} can't be used.`);
            });
        return () => { cancelled = true; };
    }, [stage, signupPromoCode, onboardingTiers]);
    const signupPromoFor = (tierId) => signupPromo?.tiers?.find((t) => t.TierID === tierId) || null;

    const submitStage3 = async (e) => {
        e.preventDefault();
        setTierError("");
        const tier = onboardingTiers.find((t) => t.TierID === selectedTierId);
        if (!tier) {
            setTierError("Please choose a membership option.");
            return;
        }

        const isFreeForever = Number(tier.Price) === 0 && !tier.TrialDays;
        setTierSubmitting(true);
        try {
            // The signup code makes this paid plan free: claim it with the code
            if (signupPromo && signupPromo.discountType === "free" && signupPromoFor(tier.TierID) && Number(tier.Price) > 0) {
                await api.post("/membership/code/redeem", { code: signupPromo.code, tier_id: tier.TierID }, { withCredentials: true });
                window.location.href = "/membership/free-success";
                return;
            }
            if (isFreeForever) {
                //await api.post("/membership/select-free-tier", {}, { withCredentials: true });
                // Send the free plan picked here; without it the server grants the default one
                await api.post("/membership/select-free-tier", { tier_id: tier.TierID }, { withCredentials: true });
                window.location.href = "/membership/free-success";
                return;
            }

            //const customerEmail = isDashboardOnboardingMode ? (user?.email || "") : (registeredEmail || creds.email);
            // A resumed signup (after login, /verify-email or ?fromDashboard=1) never
            // ran stage 1 here, and the user from the login response has no email, so
            // fall back to /user/me (same as the plans page does).
            let customerEmail = registeredEmail || creds.email || user?.email || "";
            if (!customerEmail) {
                try {
                    const me = await api.get("/user/me");
                    customerEmail = me.data?.email || "";
                } catch { /* the checkout call below reports it */ }
            }
            const response = await api.post("/membership/create-checkout-session", {
                price_id: tier.PaymentLink,
                tier_id: tier.TierID,
                // A percent/amount signup code lowers this checkout
                promo_code: signupPromo && signupPromo.discountType !== "free" && signupPromoFor(tier.TierID) ? signupPromo.code : undefined,
                customer_email: customerEmail,
                attempt_id: tierAttemptId || undefined,
            }, { withCredentials: true });

            // Hold onto the attempt_id so a retry (network blip, double-click)
            // reuses the same in-progress attempt instead of starting a new one.
            if (response.data?.attempt_id) setTierAttemptId(response.data.attempt_id);

            if (response.data?.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                setTierError("Could not start checkout. Please try again.");
            }
        } catch (err) {
            setTierError(err.response?.data?.error || "Could not start checkout. Please try again.");
        } finally {
            setTierSubmitting(false);
        }
    };

    return (
        <div className={`min-h-screen transition-colors duration-500 ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>
            <main className={`mx-auto px-4 py-16 ${stage === 3 ? 'max-w-5xl' : 'max-w-2xl'}`}>

                {!isDashboardOnboardingMode && (
                    <div className="flex items-center justify-center mb-12 gap-4">
                        <div
                            className={`h-3 w-3 rounded-full ${
                                stage === 1 || stage === "verify" || stage === 2 || stage === 3 ? "bg-primary-500" : "bg-slate-300"
                            }`}
                        />
                        <div className={`h-1 w-20 rounded ${stage === 2 || stage === 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                        <div className={`h-3 w-3 rounded-full ${stage === 2 || stage === 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                        <div className={`h-1 w-20 rounded ${stage === 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                        <div className={`h-3 w-3 rounded-full ${stage === 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                    </div>
                )}

                {stage !== 3 && (
                <div className={`rounded-3xl border p-10 shadow-2xl backdrop-blur-sm ${isDarkMode ? 'bg-slate-900/80 border-slate-800 shadow-slate-950/40' : 'bg-white/90 border-slate-100 shadow-slate-300/30'}`}>
                    {stage === "verify" && (
                        <div className="animate-in fade-in duration-500 py-8">
                            <VerifyCodeForm
                                email={registeredEmail}
                                onVerified={() => setStage(2)}
                                verifyEndpoint={signupType === 'coach' ? "/auth/signup/coach/verify" : "/auth/verify-email"}
                                resendEndpoint={signupType === 'coach' ? "/auth/signup/coach/resend" : "/auth/resend-verification"}
                                description={signupType === 'coach'
                                    ? "Enter the code to confirm your email. Our team will review your coach application next."
                                    : "Enter the code to confirm your email, then you'll continue to profile setup."}
                            />
                        </div>
                    )}

                    {stage === 1 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {/* First step only: a way back for anyone who opened signup
                                by mistake. Later steps (the account already exists) do
                                not show this on purpose. */}
                            <Link
                                to="/"
                                aria-label="Back to home"
                                title="Back to home"
                                className="inline-flex items-center justify-center w-10 h-10 mb-6 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-300 transition-colors"
                            >
                                <ArrowLeft size={18} />
                            </Link>
                            <h2 className="text-3xl font-extrabold mb-2">Create Your Account</h2>
                            <p className="text-slate-500 mb-6">Let's start with your basic information.</p>

                            {/* Program invitation link: who invited them. Invitations are for student accounts, so the role toggle is hidden. */}
                            {invitedProgram && (
                                <div className="mb-8 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-slate-600">
                                    {invitedProgram.kind === "license" ? (
                                        <>
                                            <span className="font-semibold text-slate-800">{invitedProgram.OrgName}</span> gave you a license
                                            {invitedProgram.ProgramName && <> for <span className="font-semibold text-slate-800">{invitedProgram.ProgramName}</span></>}.
                                            {" "}Create your account and it's yours once your profile is set up.
                                        </>
                                    ) : (
                                        <>
                                            You're joining <span className="font-semibold text-slate-800">{invitedProgram.ProgramName}</span> at{" "}
                                            <span className="font-semibold text-slate-800">{invitedProgram.OrgName}</span>. Create your account and you'll be added once your profile is set up.
                                        </>
                                    )}
                                </div>
                            )}
                            {emailInvite && (
                                <div className="mb-8 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-slate-600">
                                    <span className="font-semibold text-slate-800">{emailInvite.OrgName}</span> invited you
                                    {invitedCoach ? " to join as a coach" : emailInvite.ProgramName && <> to <span className="font-semibold text-slate-800">{emailInvite.ProgramName}</span></>}.
                                    {invitedCoach
                                        ? <> Create your coach account with {emailInvite.Email}; a certification is optional. You'll join {emailInvite.OrgName} once you verify your email, and you can sign in after Build My Biz Brand approves your account.</>
                                        : <> Create your account with {emailInvite.Email} and you'll be added once your profile is set up.</>}
                                </div>
                            )}
                            {emailInviteError && (
                                <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                    {emailInviteError.message}
                                    {emailInviteError.used && <> <Link to="/login" className="font-semibold underline">Log in</Link></>}
                                </div>
                            )}
                            {inviteInvalid && (
                                <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                    {inviteInvalid === "used"
                                        ? <>That code was already used. If it's yours, <Link to="/login" className="font-semibold underline">log in</Link> instead.</>
                                        : "This invitation link doesn't work anymore. You can still create an account and enter a new code later under My Coaching → Programs."}
                                </div>
                            )}

                            {/* Role Toggle */}
                            {!invitedProgram && !emailInvite && (
                            <div className={`flex rounded-xl overflow-hidden border mb-8 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                <button
                                    type="button"
                                    onClick={() => setSignupType('student')}
                                    className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${signupType === 'student' ? 'bg-primary-600 text-white' : (isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-50')}`}
                                >
                                    Student / Job Seeker
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSignupType('coach')}
                                    className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${signupType === 'coach' ? 'bg-primary-600 text-white' : (isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-50')}`}
                                >
                                    Coach
                                </button>
                            </div>
                            )}

                            {signupType === 'coach' && !invitedCoach && (
                                <div className="mb-8 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-slate-600 space-y-2">
                                    <p>You'll need to hold an active ICF or Gallup certification to partner with our organization and register as a coach.</p>
                                    <p>Please complete the sign up process and you will be able to log in once we have verified your credentials.</p>
                                </div>
                            )}

                            <form onSubmit={submitStage1} className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    {/* First Name */}
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">First Name</label>
                                        <input
                                            name="firstName"
                                            value={creds.firstName}
                                            onChange={handleCredChange}
                                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.firstName ? 'border-red-500' : 'border-slate-300'}`}
                                        />
                                        {errors.firstName && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.firstName}</p>}
                                    </div>

                                    {/* Last Name */}
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">Last Name</label>
                                        <input
                                            name="lastName"
                                            value={creds.lastName}
                                            onChange={handleCredChange}
                                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.lastName ? 'border-red-500' : 'border-slate-300'}`}
                                        />
                                        {errors.lastName && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.lastName}</p>}
                                    </div>
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Email Address{signupType === 'coach' && <span className="text-slate-400 font-normal"> (This will be your Sign In Name)</span>}</label>
                                    <input
                                        name="email"
                                        type="email"
                                        value={creds.email}
                                        onChange={handleCredChange}
                                        onBlur={() => setEmailTouched(true)}
                                        readOnly={!!emailInvite}
                                        title={emailInvite ? "Your invitation was sent to this address" : undefined}
                                        className={`w-full rounded-xl border p-3 transition-colors ${emailInvite ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-transparent'} ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                                    />
                                    {errors.email && errors.email !== "Please enter a valid email address format (e.g., user@domain.com)." && (
                                        <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.email}</p>
                                    )}

                                    {/* Live Email Format Hint: red while invalid, green checkmark once valid.
                                        Only shown once the field has been blurred, so it doesn't flag every keystroke. */}
                                    {creds.email && emailTouched && (
                                        <p className={`mt-1 ml-1 text-xs font-medium ${emailRegex.test(creds.email) ? 'text-primary-500' : 'text-red-500'}`}>
                                            {emailRegex.test(creds.email) ? "✓ Valid email" : "Please enter a valid email address format (e.g., user@domain.com)."}
                                        </p>
                                    )}
                                </div>

                                {/* Directory Email (coach only) — stored on the coach's Partners row. */}
                                {signupType === 'coach' && (
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">
                                            Directory Email Address <span className="text-slate-400 font-normal">(This will be listed publicly in our Coach Directory)</span>
                                        </label>
                                        <input
                                            name="directoryEmail"
                                            type="email"
                                            value={coachInfo.directorySameAsSignIn ? (creds.email || '') : coachInfo.directoryEmail}
                                            onChange={(e) => handleCoachFieldChange('directoryEmail', e.target.value)}
                                            disabled={coachInfo.directorySameAsSignIn}
                                            className={`w-full rounded-xl border p-3 transition-colors ${coachInfo.directorySameAsSignIn ? 'bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed' : `bg-transparent ${errors.directoryEmail ? 'border-red-500' : 'border-slate-300'}`}`}
                                        />
                                        <label className="flex items-center gap-2.5 mt-2 text-sm font-medium text-slate-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={coachInfo.directorySameAsSignIn}
                                                onChange={(e) => handleCoachFieldChange('directorySameAsSignIn', e.target.checked)}
                                                className="h-5 w-5 accent-primary-600"
                                            />
                                            Same as my sign-in email
                                        </label>
                                        {errors.directoryEmail && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.directoryEmail}</p>}
                                    </div>
                                )}

                                {/* Password — for coach signups this moves below the State field instead (see below). */}
                                {signupType !== 'coach' && (
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">Password</label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                value={creds.password}
                                                onChange={handleCredChange}
                                                className={`w-full rounded-xl border p-3 pr-11 bg-transparent transition-colors ${errors.password ? 'border-red-500' : 'border-slate-300'}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                tabIndex={-1}
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {errors.password && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.password}</p>}

                                        {/* Live Password Strength Hint */}
                                        {creds.password && (
                                            <p className={`mt-1 ml-1 text-xs font-medium ${creds.password.length >= 8 && /[A-Z]/.test(creds.password) ? 'text-primary-500' : 'text-slate-400'}`}>
                                                {creds.password.length >= 8 && /[A-Z]/.test(creds.password) ? "✓ Requirements Met" : "Requires 8+ characters and one uppercase letter"}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Referral Code (Optional) - not applicable to coach signups */}
                                {signupType !== 'coach' && (
                                    <div>
                                        {/* <label className="text-sm font-semibold mb-1 block">Referral Code <span className="text-slate-400 font-normal">(optional)</span></label> */}
                                        {/* the box also takes a license code from a school or organization */}
                                        <label className="text-sm font-semibold mb-1 block">Referral, promo or license code <span className="text-slate-400 font-normal">(optional)</span></label>
                                        <input
                                            name="referralCode"
                                            value={creds.referralCode}
                                            onChange={handleCredChange}
                                            placeholder="e.g. ABC12345"
                                            //maxLength={20}
                                            maxLength={32}
                                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors font-mono tracking-widest uppercase border-slate-300 placeholder:text-slate-300 placeholder:tracking-normal placeholder:font-sans`}
                                        />
                                    </div>
                                )}

                                {signupType === 'coach' && (
                                    <>
                                        {/* Credential Type (required radio group) */}
                                        <div>
                                            <label className="text-sm font-semibold mb-2 block">
                                                Credential Type {invitedCoach ? <span className="font-normal text-slate-400">(optional)</span> : <span className="text-red-500">*</span>}
                                            </label>
                                            <div className="space-y-2">
                                                {COACH_CREDENTIALS.map((opt) => (
                                                    <label
                                                        key={opt.value}
                                                        className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                                            coachInfo.credentialType === opt.value
                                                                ? 'border-primary-600 bg-primary-50'
                                                                : errors.credentialType
                                                                    ? 'border-red-500'
                                                                    : 'border-slate-300 hover:border-slate-400'
                                                        }`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="credentialType"
                                                            value={opt.value}
                                                            checked={coachInfo.credentialType === opt.value}
                                                            onChange={() => handleCoachFieldChange('credentialType', opt.value)}
                                                            className="h-4 w-4 accent-primary-600"
                                                        />
                                                        <span className="text-sm font-medium">{opt.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {errors.credentialType && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.credentialType}</p>}
                                        </div>

                                        {/* Certificate URL (required, no format validation) */}
                                        <div>
                                            <label className="text-sm font-semibold mb-1 block">
                                                Certificate URL {invitedCoach ? <span className="font-normal text-slate-400">(optional)</span> : <span className="text-red-500">*</span>}
                                            </label>
                                            <input
                                                type="text"
                                                name="certificateUrl"
                                                value={coachInfo.certificateUrl}
                                                onChange={(e) => handleCoachFieldChange('certificateUrl', e.target.value)}
                                                placeholder=""
                                                className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.certificateUrl ? 'border-red-500' : 'border-slate-300'}`}
                                            />
                                            {errors.certificateUrl && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.certificateUrl}</p>}
                                        </div>

                                        {/* Company Name (optional) */}
                                        <div>
                                            <label className="text-sm font-semibold mb-1 block">
                                                Company Name <span className="text-slate-400 font-normal">(optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                value={coachInfo.companyName}
                                                onChange={(e) => handleCoachFieldChange('companyName', e.target.value)}
                                                placeholder=""
                                                maxLength={200}
                                                className="w-full rounded-xl border p-3 bg-transparent transition-colors border-slate-300"
                                            />
                                        </div>

                                        {/* State (required dropdown) */}
                                        <SingleSelect
                                            label="State"
                                            required
                                            value={coachInfo.state}
                                            options={US_STATE_OPTIONS}
                                            placeholder="Select your state..."
                                            onChange={(value) => handleCoachFieldChange('state', value)}
                                            error={errors.state}
                                            isDarkMode={isDarkMode}
                                            searchable
                                            searchPlaceholder="Search states..."
                                        />

                                        {/* Password — placed right after State so credential/state
                                            fields stay grouped before the account password. */}
                                        <div>
                                            <label className="text-sm font-semibold mb-1 block">Password</label>
                                            <div className="relative">
                                                <input
                                                    name="password"
                                                    type={showPassword ? "text" : "password"}
                                                    value={creds.password}
                                                    onChange={handleCredChange}
                                                    className={`w-full rounded-xl border p-3 pr-11 bg-transparent transition-colors ${errors.password ? 'border-red-500' : 'border-slate-300'}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                            {errors.password && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.password}</p>}

                                            {/* Live Password Strength Hint */}
                                            {creds.password && (
                                                <p className={`mt-1 ml-1 text-xs font-medium ${creds.password.length >= 8 && /[A-Z]/.test(creds.password) ? 'text-primary-500' : 'text-slate-400'}`}>
                                                    {creds.password.length >= 8 && /[A-Z]/.test(creds.password) ? "✓ Requirements Met" : "Requires 8+ characters and one uppercase letter"}
                                                </p>
                                            )}
                                        </div>

                                        {/* Mandatory attestation */}
                                        <div>
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={coachInfo.attested}
                                                    onChange={(e) => handleCoachFieldChange('attested', e.target.checked)}
                                                    className="h-4 w-4 mt-0.5 accent-primary-600 shrink-0"
                                                />
                                                <span className="text-sm text-slate-600">
                                                    {invitedCoach
                                                        ? "I consent to share my contact information with Build My Biz Brand members via the Build My Biz Brand Coach Directory (and, if I entered a certification above, I attest that it's active)."
                                                        : "I attest that I hold an active ICF or Gallup certification, and I consent to share my contact information with Build My Biz Brand members via the Build My Biz Brand Coach Directory."}
                                                    {!invitedCoach && <span className="text-red-500"> *</span>}
                                                </span>
                                            </label>
                                            {errors.attested && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.attested}</p>}
                                        </div>
                                    </>
                                )}

                                {/* Required for every signup type — validated in validateStage1
                                    so the account can't be created without checking it. */}
                                <div>
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={creds.acceptedTerms}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setCreds((prev) => ({ ...prev, acceptedTerms: checked }));
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.acceptedTerms;
                                                    return next;
                                                });
                                            }}
                                            className="h-4 w-4 mt-0.5 accent-primary-600 shrink-0"
                                        />
                                        <span className="text-sm text-slate-600">
                                            I agree to the{' '}
                                            <a
                                                href="https://buildmybizbrand.com/terms-of-service"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-primary-600 underline hover:text-primary-700"
                                            >
                                                Terms of Service
                                            </a>{' '}
                                            and{' '}
                                            <a
                                                href="https://buildmybizbrand.com/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-primary-600 underline hover:text-primary-700"
                                            >
                                                Privacy Policy
                                            </a>.<span className="text-red-500"> *</span>
                                        </span>
                                    </label>
                                    {errors.acceptedTerms && <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.acceptedTerms}</p>}
                                </div>

                                <button
                                    type="submit"
                                    disabled={!isStage1Ready || stage1Submitting}
                                    className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition-all active:scale-[0.98] ${
                                        isStage1Ready && !stage1Submitting
                                            ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-900/20'
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                    }`}
                                >
                                    {stage1Submitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            {signupType === 'student' ? 'Creating Account...' : 'Submitting...'}
                                        </>
                                    ) : (
                                        signupType === 'student' ? 'Next Step: Profile Setup →' : 'Create Account →'
                                    )}
                                </button>
                            </form>
                        </div>
                    )}

                    {stage === 2 && signupType === 'coach' && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 text-center py-4">
                            <div className={`mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full ${isDarkMode ? 'bg-primary-950/40' : 'bg-primary-50'}`}>
                                <FiCheckCircle className="text-primary-600" size={28} />
                            </div>
                            <h2 className="text-3xl font-extrabold mb-2">Application Received</h2>
                            <p className={`mb-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Your email is verified and your coach application is submitted. Our team
                                will review it and activate your account shortly — you'll be able to sign
                                in once it's approved.
                            </p>
                            <button
                                type="button"
                                onClick={() => { window.location.href = '/login'; }}
                                className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98]"
                            >
                                Back to Sign In →
                            </button>
                        </div>
                    )}

                    {stage === 2 && signupType === 'student' && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                            {stage2SessionMissing && !isDashboardOnboardingMode && (
                                <div
                                    className={`mb-6 rounded-2xl border p-4 text-left ${
                                        isDarkMode
                                            ? "border-amber-800/60 bg-amber-950/40 text-amber-100"
                                            : "border-amber-200 bg-amber-50 text-amber-950"
                                    }`}
                                >
                                    <p className="font-semibold text-sm">We couldn&apos;t confirm your session</p>
                                    <p className={`text-sm mt-2 ${isDarkMode ? "text-amber-200/90" : "text-amber-900/90"}`}>
                                        Log in with the account you registered, or open the verification link again in this browser
                                        so we can save your profile.
                                    </p>
                                    <Link
                                        to="/login"
                                        className={`mt-3 inline-block text-sm font-semibold underline ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}
                                    >
                                        Go to log in
                                    </Link>
                                </div>
                            )}
                            {isStage2FromUrl && verifiedFromEmail && !isDashboardOnboardingMode && (
                                <div
                                    className={`mb-6 flex items-start gap-3 rounded-2xl border p-4 text-left ${
                                        isDarkMode
                                            ? "border-primary-800/50 bg-primary-950/40 text-primary-100"
                                            : "border-primary-200 bg-primary-50 text-primary-900"
                                    }`}
                                >
                                    <FiCheckCircle className="shrink-0 text-primary-600 mt-0.5" size={22} />
                                    <div>
                                        <p className="font-semibold text-sm">Email verified</p>
                                        <p className={`text-sm mt-1 ${isDarkMode ? "text-primary-200/90" : "text-primary-800/90"}`}>
                                            Complete the steps below. After signup you can continue to your onboarding tasks from the
                                            dashboard.
                                        </p>
                                    </div>
                                </div>
                            )}
                            <h2 className="text-3xl font-extrabold mb-2">Profile Setup</h2>
                            <p className="text-slate-500 mb-3">
                                {isDashboardOnboardingMode ? "Complete your onboarding details to unlock recommendations." : "Thanks for signing up. One last step ... tell us about your aspirations. These details will help your AI coach learn about your goals and will influence your job recommendations and career task suggestions."}
                            </p>
                            <p className="text-slate-500 mb-3">
                                {isDashboardOnboardingMode ? "" : "Note: Our AI coach was built to help users compete in the new age of AI. Additional personalization for non-technical roles coming soon."}
                            </p>
                            <p className="text-slate-500 mb-8">
                                {isDashboardOnboardingMode ? "" : "You can watch this helpful video "} <a className="text-blue-500 underline italic" href="https://www.youtube.com/watch?v=W_8V3oBblAs" target="_blank" rel="noopener noreferrer">How to Sign Up for Biz Brand App</a>
                            </p>

                            <form onSubmit={submitStage2} className="space-y-6">
                                {/* Aspiring Role - Dropdown from JobRoles table */}
                                <SingleSelect
                                    label="Aspiring Role"
                                    value={profile.aspiringRole}
                                    options={roleOptions}
                                    placeholder={isLoadingLookups ? "Loading roles..." : roleOptions.length === 0 ? "No roles available" : "Select a role..."}
                                    onChange={(value) => handleProfileFieldChange("aspiringRole", value)}
                                    error={errors.aspiringRole}
                                    disabled={isLoadingLookups || roleOptions.length === 0}
                                    helperText={!profile.aspiringRole ? "Choose the role you're aspiring to" : ""}
                                    isDarkMode={isDarkMode}
                                />

                                {/* Interests / Target Industries Multi-Select */}
                                <MultiSelect
                                    label="Industries"
                                    options={industryNames}
                                    selected={profile.industries}
                                    onChange={(selected) => {
                                        setErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.industries;
                                            return next;
                                        });
                                        setProfile((prev) => ({ ...prev, industries: selected }));
                                    }}
                                    placeholder={isLoadingLookups ? "Loading interests..." : industryOptions.length === 0 ? "No interests available" : "Select one or more industries..."}
                                    error={errors.industries}
                                    disabled={isLoadingLookups || industryOptions.length === 0}
                                    isDarkMode={isDarkMode}
                                    matchFieldStyle
                                />

                                <div className="grid grid-cols-2 gap-6">
                                    {/* Dynamic Persona Selection */}
                                    <SingleSelect
                                        label="Job Seeker Cohort"
                                        value={profile.persona}
                                        options={personaOptions}
                                        placeholder={isLoadingLookups ? "Loading cohorts..." : personaOptions.length === 0 ? "No cohorts available" : "Choose stage..."}
                                        onChange={(value) => handleProfileFieldChange("persona", value)}
                                        error={errors.persona}
                                        disabled={isLoadingLookups || personaOptions.length === 0}
                                        isDarkMode={isDarkMode}
                                        rightLabel={profile.persona ? <InfoTooltip text={lookups.personas[profile.persona]?.description} isDarkMode={isDarkMode} /> : null}
                                        maxVisibleOptions={3}
                                    />

                                    {/* Dynamic Intent Selection */}
                                    <SingleSelect
                                        label="Career Goal"
                                        value={profile.intent}
                                        options={intentOptions}
                                        placeholder={isLoadingLookups ? "Loading goals..." : intentOptions.length === 0 ? "No goals available" : "Choose focus..."}
                                        onChange={(value) => handleProfileFieldChange("intent", value)}
                                        error={errors.intent}
                                        disabled={isLoadingLookups || intentOptions.length === 0}
                                        isDarkMode={isDarkMode}
                                        rightLabel={profile.intent ? <InfoTooltip text={lookups.intents[profile.intent]?.description} isDarkMode={isDarkMode} /> : null}
                                        maxVisibleOptions={3}
                                    />
                                </div>

                                {/* Preferred Locations (up to 3) */}
                                <div>
                                    <label className="text-sm font-semibold block">
                                        Preferred Locations
                                        <span className="text-slate-400 font-normal ml-1">(up to 3)</span>
                                    </label>
                                    <p className="text-xs text-slate-500 mb-3">Select your preferred job locations</p>
                                    
                                    {/* Selected locations tags */}
                                    {selectedLocations.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {selectedLocations.map(loc => (
                                                <span
                                                    key={loc.id}
                                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                                                        isDarkMode
                                                            ? 'bg-primary-900/50 text-primary-300 border border-primary-700'
                                                            : 'bg-primary-50 text-primary-700 border border-primary-200'
                                                    }`}
                                                >
                                                    {loc.city}, {loc.state}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLocation(loc.id)}
                                                        className="ml-1 hover:text-red-500 transition-colors font-bold"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {selectedLocations.length < 3 && (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-4">
                                                {/* State dropdown */}
                                                <SingleSelect
                                                    label="State"
                                                    value={currentState}
                                                    options={locationsByState.map(s => ({ value: s.state, label: s.state }))}
                                                    placeholder={locationsByState.length === 0 ? "No states available" : "Select state..."}
                                                    onChange={(value) => { setCurrentState(value); setCurrentCity(""); }}
                                                    disabled={locationsByState.length === 0}
                                                    isDarkMode={isDarkMode}
                                                    maxVisibleOptions={5}
                                                    searchable
                                                    searchPlaceholder="Search states..."
                                                />

                                                {/* City dropdown */}
                                                <SingleSelect
                                                    label="City"
                                                    value={currentCity}
                                                    options={citiesForState
                                                        .filter(c => !selectedLocations.some(l => l.id === c.id))
                                                        .map(c => ({ value: String(c.id), label: c.name }))}
                                                    placeholder={!currentState ? "Select a state first" : "Select city..."}
                                                    onChange={(value) => setCurrentCity(value)}
                                                    disabled={!currentState}
                                                    isDarkMode={isDarkMode}
                                                    maxVisibleOptions={5}
                                                    searchable
                                                    searchPlaceholder="Search cities..."
                                                />
                                            </div>

                                            {/* Add button */}
                                            <button
                                                type="button"
                                                onClick={addLocation}
                                                disabled={!currentCity}
                                                className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all ${
                                                    currentCity
                                                        ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                                                        : (isDarkMode ? 'bg-slate-800 text-slate-600' : 'bg-slate-100 text-slate-400')
                                                }`}
                                            >
                                                + Add Location
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* {selectedLocations.length === 0 && (
                                        <p className="text-xs text-slate-500 mt-1">Select your preferred job locations</p>
                                    )} */}
                                </div>
                                {isDashboardOnboardingMode && (
                                    <button
                                        type="button"
                                        onClick={backToDashboard}
                                        disabled={backLoading}
                                        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50"
                                    >
                                        {backLoading ? 'Leaving…' : '← Back to Dashboard'}
                                    </button>
                                )}
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={skipStage2}
                                        //old disabled={skipLoading}
                                        disabled={skipLoading || stage2Submitting}
                                        className="w-full bg-black text-white py-4 rounded-xl font-bold hover:opacity-75 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
                                    >
                                        {skipLoading ? 'Skipping...' : 'Skip for now'}
                                    </button>
                                    <button
                                        type="submit"
                                        //old disabled={!isStage2Ready || stage2Submitting}
                                        disabled={!isStage2Ready || stage2Submitting || skipLoading}
                                        className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition-all active:scale-[0.98] ${
                                            //old isStage2Ready && !stage2Submitting
                                            isStage2Ready && !stage2Submitting && !skipLoading
                                                ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg'
                                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {stage2Submitting ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                {isDashboardOnboardingMode ? 'Saving...' : 'Submitting...'}
                                            </>
                                        ) : (
                                            isDashboardOnboardingMode ? 'Save Onboarding' : 'Submit Onboarding'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {errors.server && <p className="mt-4 text-red-500 text-sm text-center font-medium">⚠️ {errors.server}</p>}
                </div>
                )}

                {stage === 3 && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        {/* An organization license is checked first, so a covered student never sees the plans */}
                        {coveredBy === undefined ? (
                            <PlanStepSkeleton isDarkMode={isDarkMode} heading />
                        ) : coveredBy ? (
                            <div className={`mx-auto max-w-2xl rounded-3xl border p-10 text-center shadow-2xl backdrop-blur-sm ${isDarkMode ? 'bg-slate-900/80 border-slate-800 shadow-slate-950/40' : 'bg-white/90 border-slate-100 shadow-slate-300/30'}`}>
                                <div className={`mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full ${isDarkMode ? 'bg-primary-950/40' : 'bg-primary-50'}`}>
                                    <FiCheckCircle className="text-primary-600" size={28} />
                                </div>
                                <h2 className="text-3xl font-extrabold mb-2">Your plan is covered</h2>
                                <p className={`mb-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    <span className={`font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{coveredBy.OrgName}</span> covers your
                                    Build My Biz Brand plan{coveredBy.SponsoredTierName ? ` (${coveredBy.SponsoredTierName})` : ''} while your license is active.
                                    {" "}You don't need to pick or pay for a plan.
                                </p>
                                {tierError && <p className="mb-4 text-red-500 text-sm font-medium">{tierError}</p>}
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setStage(2)}
                                        disabled={tierSubmitting}
                                        className={`px-6 py-4 rounded-xl font-bold border transition-all active:scale-[0.98] disabled:opacity-50 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        ← Back
                                    </button>
                                    {/* waits for the plans, so it sends the same free plan the plan cards would */}
                                    <button
                                        type="button"
                                        onClick={continueCovered}
                                        disabled={tierSubmitting || isLoadingTiers}
                                        className="flex-1 bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
                                    >
                                        {tierSubmitting ? 'Continuing...' : 'Go to my dashboard'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <>
                        <h2 className="text-3xl font-extrabold mb-2">Choose Your Membership</h2>
                        <p className="text-slate-500 mb-8">Pick a plan to get started, you can change this later from your Membership page.</p>
                        {/* <p className="text-slate-500 mb-8">Lucky you! We are current offering free trial access for 60 days during our Beta, ending Oct. 1st.  Please be sure to select either the Pro - Beta or Pro - Beta (annual) plan.</p> */}

                        <form onSubmit={submitStage3} className="space-y-6">
                            {isLoadingTiers ? (
                                //old <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading options...</p>
                                <PlanStepSkeleton isDarkMode={isDarkMode} />
                            ) : onboardingTiers.length === 0 ? (
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No membership options are available right now.</p>
                            ) : (
                                <>
                                    {signupPromo && (
                                        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-600'}`}>
                                            <span><span className="font-bold">{signupPromo.code}</span> applied to the plans marked below.</span>
                                            <button type="button" onClick={() => { setSignupPromo(null); setSignupPromoCode(""); }} className="font-semibold text-primary-600 hover:underline">Remove</button>
                                        </div>
                                    )}
                                    {signupPromoError && <p className="text-red-500 text-sm font-medium">{signupPromoError}</p>}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                                        {[...onboardingTiers]
                                            .sort((a, b) => Number(a.Price) - Number(b.Price))
                                            .map((t, idx) => {
                                                //const featureInfo = getFeaturesForTier(idx);
                                                // the CSI line follows what this plan really unlocks
                                                const featureInfo = withTierAccess(getFeaturesForTier(idx), t);
                                                const features = MEMBERSHIP_FEATURES.map((label, i) => ({ label, included: featureInfo[i].included, badge: featureInfo[i].badge }));
                                                // "Most Popular" is the flag admins set in Edit Tier (same as the plans page)
                                                const mostPopular = t.IsMostPopular === true || t.IsMostPopular === 1;
                                                return (
                                                    <MembershipCard
                                                        key={t.TierID}
                                                        tier={t}
                                                        features={features}
                                                        selected={selectedTierId === t.TierID}
                                                        highlighted={mostPopular}
                                                        badgeText={mostPopular ? 'Most Popular' : undefined}
                                                        onSelect={() => setSelectedTierId(t.TierID)}
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        {signupPromoFor(t.TierID) && (
                                                            <p className="mt-2 text-xs font-bold uppercase tracking-wide text-success">
                                                                {signupPromo.code} &middot; ${Number(signupPromoFor(t.TierID).DiscountedPrice).toFixed(2)}{t.BillingFrequency === 'yearly' ? '/yr' : '/mo'}
                                                                {signupPromoFor(t.TierID).DiscountEndsOn ? ` until ${new Date(`${signupPromoFor(t.TierID).DiscountEndsOn}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                                                            </p>
                                                        )}
                                                    </MembershipCard>
                                                );
                                            })}
                                    </div>
                                    {tierError && <p className="text-red-500 text-sm font-medium">{tierError}</p>}

                                    {(() => {
                                        const selectedTier = onboardingTiers.find((t) => t.TierID === selectedTierId);
                                        if (!selectedTier) return null;
                                        const price = Number(selectedTier.Price);
                                        const isFreeForever = price === 0 && !selectedTier.TrialDays;
                                        //const priceLabel = price === 0 ? 'free' : `$${price.toFixed(2)}/mo`;
                                        //const priceLabel = price === 0 ? 'free' : `$${price.toFixed(2)}${selectedTier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}`;
                                        const fullPriceLabel = price === 0 ? 'free' : `$${price.toFixed(2)}${selectedTier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}`;
                                        // The signup code: a free code gives the plan, a percent/amount code
                                        // lowers the price until the code ends
                                        const codeTier = signupPromoFor(selectedTier.TierID);
                                        const codeFree = codeTier && signupPromo.discountType === 'free' && price > 0;
                                        const priceLabel = codeTier && !codeFree && price > 0
                                            ? `$${Number(codeTier.DiscountedPrice).toFixed(2)}${selectedTier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}${codeTier.DiscountEndsOn ? ` until ${new Date(`${codeTier.DiscountEndsOn}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, then ${fullPriceLabel}` : ''}`
                                            : fullPriceLabel;
                                        return (
                                            <div className={`rounded-xl border p-4 text-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                                <p>
                                                    {/* {isFreeForever ? ( */}
                                                    {codeFree ? (
                                                        <>You're getting <span className="font-bold">{selectedTier.TierName}</span> free with <span className="font-bold">{signupPromo.code}</span>.</>
                                                    ) : isFreeForever ? (
                                                        <>You're selecting <span className="font-bold">{selectedTier.TierName}</span> — free, no cost.</>
                                                    ) : selectedTier.TrialDays ? (
                                                        <>You're selecting <span className="font-bold">{selectedTier.TierName}</span> free for <span className="font-bold">{selectedTier.TrialDays} days</span>, then billed <span className="font-bold">{priceLabel}</span>.</>
                                                    ) : (
                                                        <>You're selecting <span className="font-bold">{selectedTier.TierName}</span> for <span className="font-bold">{priceLabel}</span>.</>
                                                    )}
                                                </p>
                                                <p className="mt-1">You can cancel your membership and future payments anytime.</p>
                                            </div>
                                        );
                                    })()}
                                </>
                            )}

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setStage(2)}
                                    disabled={tierSubmitting}
                                    className={`px-6 py-4 rounded-xl font-bold border transition-all active:scale-[0.98] disabled:opacity-50 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    ← Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={tierSubmitting || isLoadingTiers || onboardingTiers.length === 0}
                                    className="flex-1 bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
                                >
                                    {tierSubmitting ? 'Continuing...' : 'Continue'}
                                </button>
                            </div>
                        </form>
                        </>
                        )}

                        {errors.server && <p className="mt-4 text-red-500 text-sm text-center font-medium">⚠️ {errors.server}</p>}
                    </div>
                )}
            </main>
        </div>
    );
}
